import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ModalDialog } from '../../job-roles/components/ModalDialog';
import { ConfirmDialog } from '../../job-roles/components/ConfirmDialog';
import { RetryableError } from '../../../shared/RetryableError';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { dayAfter, formatDate, todayIso } from '../dates';
import { benefitsGateway } from '../index';
import { loadAllPlans } from '../loaders';
import { electionStatus } from '../planSummary';
import { useEmployeeDirectory } from '../useEmployeeDirectory';
import { coverageLevelLabels, type BenefitElection, type BenefitPlan, type ElectionStatusFilter, type Paged } from '../types';
import { Pager } from './ListControls';
import { SelectInput, TextInput } from './fields';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

const STATUS_OPTIONS: { value: ElectionStatusFilter; label: string }[] = [
  { value: 'active', label: 'In effect' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'ended', label: 'Ended' },
  { value: 'all', label: 'All' },
];

export interface ElectionsDialogProps {
  initialPlanId?: string;
  readOnly: boolean;
  onClose: () => void;
  onChanged: () => void;
  onForbidden: () => void;
}

type Load = { kind: 'loading' } | { kind: 'ready'; data: Paged<BenefitElection> } | { kind: 'forbidden' } | { kind: 'error'; message: string };

/**
 * View Employee Elections: every benefit election in the company, joined with employee names from the Employees list and plan names from the plan list.
 * Filter by plan, employee and status; open an election's history; end an election prospectively (the history stays, nothing earlier is rewritten).
 */
export function ElectionsDialog({ initialPlanId, readOnly, onClose, onChanged, onForbidden }: ElectionsDialogProps) {
  const { state: directory, nameOf } = useEmployeeDirectory();
  const [plans, setPlans] = useState<BenefitPlan[]>([]);
  const [planId, setPlanId] = useState(initialPlanId ?? '');
  const [employeeId, setEmployeeId] = useState('');
  const [status, setStatus] = useState<ElectionStatusFilter>('active');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const [history, setHistory] = useState<{ electionId: string; rows: BenefitElection[] | 'loading' | 'error' } | null>(null);
  const [ending, setEnding] = useState<BenefitElection | null>(null);
  const [endDate, setEndDate] = useState(dayAfter(todayIso()));
  const [endError, setEndError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [writeBlocked, setWriteBlocked] = useState(false);

  useEffect(() => {
    let cancelled = false;
    loadAllPlans().then((list) => { if (!cancelled) setPlans(list); }).catch(() => undefined);
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    benefitsGateway.listElections({ planId: planId || undefined, employeeId: employeeId || undefined, status, page, pageSize }, controller.signal)
      .then((data) => { if (!controller.signal.aborted) setLoad({ kind: 'ready', data }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        if (isForbidden(err)) { onForbidden(); setLoad({ kind: 'forbidden' }); } else setLoad({ kind: 'error', message: bdErrorMessage(err, 'Could not load the elections.', 'election') });
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId, employeeId, status, page, pageSize, nonce]);

  const reload = useCallback(() => { setLoad({ kind: 'loading' }); setNonce((n) => n + 1); }, []);
  const planName = (id: string, fromRow?: string | null) => fromRow ?? plans.find((p) => p.id === id)?.name ?? 'Unknown plan';
  const today = todayIso();
  const canWrite = !readOnly && !writeBlocked;

  async function openHistory(election: BenefitElection) {
    if (history?.electionId === election.id) { setHistory(null); return; }
    setHistory({ electionId: election.id, rows: 'loading' });
    try {
      const rows = (await benefitsGateway.listEmployeeElections(election.employeeId)).filter((r) => r.benefitCodeId === election.benefitCodeId);
      setHistory({ electionId: election.id, rows });
    } catch {
      setHistory({ electionId: election.id, rows: 'error' });
    }
  }

  function startEnding(election: BenefitElection) {
    setEnding(election);
    setEndDate(dayAfter(todayIso() > election.effectiveStartDate ? todayIso() : election.effectiveStartDate));
    setEndError(null);
  }

  async function confirmEnd() {
    if (!ending) return;
    if (!endDate || endDate <= ending.effectiveStartDate) { setEndError('The end date must be after the election starts.'); return; }
    setBusy(true);
    setEndError(null);
    try {
      await benefitsGateway.endElection(ending.id, endDate);
      setNotice(`The election for ${nameOf(ending.employeeId)} on ${planName(ending.benefitCodeId)} ends on ${formatDate(endDate)}. The history is kept.`);
      setEnding(null);
      onChanged();
      reload();
    } catch (err) {
      if (isForbidden(err)) { setWriteBlocked(true); onForbidden(); }
      setEndError(bdErrorMessage(err, 'Could not end this election.', 'election'));
    } finally {
      setBusy(false);
    }
  }

  const employeeOptions = useMemo(() => [
    { value: '', label: 'All employees' },
    ...(directory.kind === 'ready' ? directory.employees.map((e) => ({ value: e.id, label: e.name })) : []),
  ], [directory]);
  const planOptions = [{ value: '', label: 'All plans' }, ...plans.map((p) => ({ value: p.id, label: p.name }))];
  const data = load.kind === 'ready' ? load.data : null;

  return (
    <ModalDialog title="Employee Elections" size="xl" onClose={onClose} busy={busy} footer={<button type="button" className="jr-btn jr-btn--primary" onClick={onClose} data-autofocus>Close</button>}>
      <p className="bd-help">Every benefit election in the company. Phase 1 treats active employees as eligible. Ending an election is prospective: the history stays and nothing earlier changes.</p>
      {writeBlocked && <p className="jr-readonly" role="status"><span aria-hidden="true">{'🔒'} </span>You can view elections but not change them. Ask a company administrator if you need to end an election.</p>}
      {notice && <p className="jr-notice jr-notice--ok" role="status"><span aria-hidden="true">{'✓'} </span>{notice}</p>}
      {load.kind === 'forbidden' ? (
        <p className="jr-error jr-error--block" role="alert">You don{'’'}t have permission to view elections. This requires permission to view employee benefits.</p>
      ) : (
        <>
          <div className="jr-filters" role="group" aria-label="Election filters">
            <SelectInput id="bd-el-plan" label="Plan" value={planId} onChange={(v) => { setPlanId(v); setPage(1); }} options={planOptions} />
            <SelectInput id="bd-el-employee" label="Employee" value={employeeId} onChange={(v) => { setEmployeeId(v); setPage(1); }} options={employeeOptions} />
            <SelectInput id="bd-el-status" label="Status" value={status} onChange={(v) => { setStatus(v as ElectionStatusFilter); setPage(1); }} options={STATUS_OPTIONS} />
          </div>
          {load.kind === 'loading' && <p className="muted" role="status">Loading elections{'…'}</p>}
          {load.kind === 'error' && <RetryableError message={load.message} onRetry={reload} />}
          {data && data.totalCount === 0 && <p className="muted" data-testid="elections-empty">No elections match these filters.</p>}
          {data && data.totalCount > 0 && (
            <>
              <div className="jr-table-scroll" role="region" aria-label="Employee elections table" tabIndex={0}>
                <table className="jr-table bd-table">
                  <caption className="jr-sr-only">Benefit elections for this company</caption>
                  <thead>
                    <tr>
                      <th scope="col">Employee</th><th scope="col">Plan</th><th scope="col">Coverage</th><th scope="col">Status</th>
                      <th scope="col">Starts</th><th scope="col">Ends</th><th scope="col">Reason</th><th scope="col">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((e) => {
                      const st = electionStatus(e, today);
                      const dash = <span className="muted" title="Not set">{'—'}</span>;
                      return (
                        <Fragment key={e.id}>
                          <tr>
                            <th scope="row">{nameOf(e.employeeId)}</th>
                            <td>{planName(e.benefitCodeId, e.planName)}</td>
                            <td>{e.coverageLevel ? coverageLevelLabels[e.coverageLevel] : dash}</td>
                            <td><span className={`badge ${st === 'Enrolled' ? 'badge--approved' : st === 'Waived' || st === 'Upcoming' ? 'badge--pending' : 'badge--neutral'}`}>{st}</span></td>
                            <td className="jr-nowrap">{formatDate(e.effectiveStartDate)}</td>
                            <td className="jr-nowrap">{e.effectiveEndDate ? formatDate(e.effectiveEndDate) : dash}</td>
                            <td className="bd-clip">{e.qualifyingEventReason ?? dash}</td>
                            <td>
                              <span className="bd-catalog__buttons">
                                <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-expanded={history?.electionId === e.id} aria-label={`History for ${nameOf(e.employeeId)} on ${planName(e.benefitCodeId, e.planName)}`} onClick={() => void openHistory(e)}>History</button>
                                {canWrite && st !== 'Ended' && (
                                  <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" aria-label={`End election for ${nameOf(e.employeeId)} on ${planName(e.benefitCodeId, e.planName)}`} onClick={() => startEnding(e)}>End election</button>
                                )}
                              </span>
                            </td>
                          </tr>
                          {history?.electionId === e.id && (
                            <tr className="bd-history">
                              <td colSpan={8}>
                                {history.rows === 'loading' && <span role="status">Loading history{'…'}</span>}
                                {history.rows === 'error' && <span role="alert">Could not load the history.</span>}
                                {Array.isArray(history.rows) && (
                                  <ul className="bd-list" aria-label={`History for ${nameOf(e.employeeId)}`}>
                                    {history.rows.map((h) => (
                                      <li key={h.id}>
                                        {formatDate(h.effectiveStartDate)} to {h.effectiveEndDate ? formatDate(h.effectiveEndDate) : 'no end date'}: {electionStatus(h, today)}
                                        {h.coverageLevel ? `, ${coverageLevelLabels[h.coverageLevel]}` : ''}{h.qualifyingEventReason ? `, ${h.qualifyingEventReason}` : ''}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <Pager page={data.page} pageSize={data.pageSize} totalCount={data.totalCount} onPage={setPage} onPageSize={(n) => { setPageSize(n); setPage(1); }} />
            </>
          )}
        </>
      )}
      {ending && (
        <ConfirmDialog title="End this election?" confirmLabel="End election" busy={busy} error={endError} onCancel={() => { if (!busy) { setEnding(null); setEndError(null); } }} onConfirm={() => void confirmEnd()}>
          <p className="bd-modal-note">
            This ends {nameOf(ending.employeeId)}{'’'}s election on {planName(ending.benefitCodeId)}. Ending is prospective: the history stays and nothing earlier changes.
          </p>
          <TextInput id="bd-el-end-date" label="Last day of the election" type="date" required value={endDate} onChange={setEndDate} hint={`It must be after ${formatDate(ending.effectiveStartDate)}.`} />
        </ConfirmDialog>
      )}
    </ModalDialog>
  );
}
